/**
 * SIM Card Management Page
 * Admin-sida för att hantera SIM-kort via backend API
 * Visar aggregerad användning, rate plans och rapporter
 * Alla Jasper API-anrop hanteras centralt via backend JasperService
 */

import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/constants/routes';
import { useTranslation } from '@/store/languageStore';
import {
 ArrowLeftIcon,
 SignalIcon,
 ArrowPathIcon,
 ChartBarIcon,
 DocumentArrowDownIcon,
 FunnelIcon,
 MagnifyingGlassIcon,
 CheckCircleIcon,
 XCircleIcon,
 ClockIcon,
 CurrencyDollarIcon,
 DevicePhoneMobileIcon,
 SignalSlashIcon,
} from '@heroicons/react/24/outline';
import { useSimCardManagement } from '@/hooks/useSimCardManagement';
import { SIM_STATUS_CONFIG } from '@/types/simCard';

const SimCardManagement = () => {
 const navigate = useNavigate();
 const { t } = useTranslation();

 const {
 simCards,
 totalCount,
 aggregatedUsage,
 ratePlans,
 loading,
 activeTab,
 statusFilter,
 searchTerm,
 page,
 pageSize,
 setActiveTab,
 setSearchTerm,
 loadAggregatedUsage,
 loadSimCards,
 createReport,
 formatDate,
 getStatusConfig,
 handleStatusFilterChange,
 goToPreviousPage,
 goToNextPage,
 } = useSimCardManagement();

 const getStatusBadge = (status?: string) => {
 const config = getStatusConfig(status);
 const StatusIcon = config.icon;

 const statusKeyMap: Record<string, string> = {
 ACTIVATED: 'simCards.statusActivated',
 ACTIVATION_READY: 'simCards.statusActivationReady',
 DEACTIVATED: 'simCards.statusDeactivated',
 INVENTORY: 'simCards.statusInventory',
 RETIRED: 'simCards.statusRetired',
 TEST_READY: 'simCards.statusTestReady',
 };
 return (
 <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.bgColor} ${config.color}`}>
 <StatusIcon className="w-3 h-3" />
 {status && statusKeyMap[status] ? t(statusKeyMap[status]) : config.label}
 </span>
 );
 };

 return (
 <div className="min-h-screen bg-bgLight">
 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
 {/* Header */}
 <div className="mb-8">
 <div className="flex items-center gap-3 mb-2">
 <button onClick={() => navigate(ROUTES.SYSTEM_ADMINISTRATION)} className="p-2 hover:bg-bgElevated rounded-lg transition-colors">
 <ArrowLeftIcon className="w-5 h-5 text-textDefault" />
 </button>
 <h1 className="text-2xl font-bold text-textDefault flex items-center gap-2">
 <SignalIcon className="w-6 h-6 text-primary" />
 {t('simCards.title')}
 </h1>
 </div>
 <p className="text-textSecondary">
 {t('simCards.description')}
 </p>
 </div>

 {/* Tabs */}
 <div className="border-b border-border mb-6">
 <nav className="-mb-px flex space-x-8">
 {[
 { id: 'overview', label: t('simCards.overview'), icon: ChartBarIcon },
 { id: 'simcards', label: t('simCards.simCards'), icon: DevicePhoneMobileIcon },
 { id: 'reports', label: t('simCards.reports'), icon: DocumentArrowDownIcon },
 ].map((tab) => (
 <button
 key={tab.id}
 onClick={() => setActiveTab(tab.id as 'overview' | 'simcards' | 'reports')}
 className={`
 flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm
 ${activeTab === tab.id
 ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
 : 'border-transparent text-textSecondary hover:text-textDefault hover:border-border dark:hover:text-textTertiary'}
 `}
 >
 <tab.icon className="w-5 h-5" />
 {tab.label}
 </button>
 ))}
 </nav>
 </div>

 {/* Overview Tab */}
 {activeTab === 'overview' && (
 <div className="space-y-6">
 {/* Summary Cards */}
 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
 <div className="bg-bgElevated rounded-xl shadow-sm border border-border p-6">
 <div className="flex items-center gap-4">
 <div className="p-3 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
 <DevicePhoneMobileIcon className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
 </div>
 <div>
 <p className="text-sm text-textSecondary">
 {t('simCards.totalSimCards')}
 </p>
 <p className="text-2xl font-bold text-textDefault">
 {loading ? '...' : (aggregatedUsage?.totalDevices || 0).toLocaleString()}
 </p>
 </div>
 </div>
 </div>

 <div className="bg-bgElevated rounded-xl shadow-sm border border-border p-6">
 <div className="flex items-center gap-4">
 <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
 <ChartBarIcon className="w-6 h-6 text-green-600 dark:text-green-400" />
 </div>
 <div>
 <p className="text-sm text-textSecondary">
 {t('simCards.totalDataUsage')}
 </p>
 <p className="text-2xl font-bold text-textDefault">
 {loading || (aggregatedUsage?.usagePending && !aggregatedUsage?.usageIncluded) ? '...' : aggregatedUsage?.usageIncluded === false ? '–' : `${(aggregatedUsage?.totalDataUsageGB || 0).toFixed(2)} GB`}
 </p>
 </div>
 </div>
 </div>

 <div className="bg-bgElevated rounded-xl shadow-sm border border-border p-6">
 <div className="flex items-center gap-4">
 <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
 <SignalIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
 </div>
 <div>
 <p className="text-sm text-textSecondary">
 {t('simCards.totalSms')}
 </p>
 <p className="text-2xl font-bold text-textDefault">
 {loading || (aggregatedUsage?.usagePending && !aggregatedUsage?.usageIncluded) ? '...' : aggregatedUsage?.usageIncluded === false ? '–' : (aggregatedUsage?.totalSmsUsage || 0).toLocaleString()}
 </p>
 </div>
 </div>
 </div>

 <div className="bg-bgElevated rounded-xl shadow-sm border border-border p-6">
 <div className="flex items-center gap-4">
 <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
 <CurrencyDollarIcon className="w-6 h-6 text-purple-600 dark:text-purple-400" />
 </div>
 <div>
 <p className="text-sm text-textSecondary">
 {t('simCards.billingCycle')}
 </p>
 <p className="text-2xl font-bold text-textDefault">
 {loading ? '...' : (aggregatedUsage?.billingCycle || '-')}
 </p>
 </div>
 </div>
 </div>
 </div>

 {/* Status Distribution */}
 {aggregatedUsage && (aggregatedUsage.activatedCount !== undefined || aggregatedUsage.deactivatedCount !== undefined) && (
 <div className="bg-bgElevated rounded-xl shadow-sm border border-border p-6">
 <h3 className="text-lg font-semibold text-textDefault mb-4">
 {t('simCards.statusDistribution')}
 </h3>
 <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
 <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
 <div className="flex items-center gap-2">
 <CheckCircleIcon className="w-5 h-5 text-green-600" />
 <p className="text-sm text-textSecondary">
 {t('simCards.activatedCount')}
 </p>
 </div>
 <p className="text-2xl font-bold text-green-700 dark:text-green-400 mt-2">
 {(aggregatedUsage.activatedCount || 0).toLocaleString()}
 </p>
 </div>
 <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
 <div className="flex items-center gap-2">
 <XCircleIcon className="w-5 h-5 text-red-600" />
 <p className="text-sm text-textSecondary">
 {t('simCards.deactivatedCount')}
 </p>
 </div>
 <p className="text-2xl font-bold text-red-700 dark:text-red-400 mt-2">
 {(aggregatedUsage.deactivatedCount || 0).toLocaleString()}
 </p>
 </div>
 <div className="p-4 bg-bgContainer rounded-lg border border-border">
 <div className="flex items-center gap-2">
 <ClockIcon className="w-5 h-5 text-textSecondary" />
 <p className="text-sm text-textSecondary">
 {t('simCards.otherCount')}
 </p>
 </div>
 <p className="text-2xl font-bold text-textDefault mt-2">
 {(aggregatedUsage.otherCount || 0).toLocaleString()}
 </p>
 </div>
 </div>

 {aggregatedUsage.lastSync && (
 <p className="text-xs text-textTertiary mt-4">
 {t('simCards.lastSynced')}: {new Date(aggregatedUsage.lastSync).toLocaleString()}
 </p>
 )}
 </div>
 )}

 {/* API Note */}
 {aggregatedUsage?.note && (
 <div className={`border rounded-lg p-4 ${aggregatedUsage.note.includes('Realtidsdata') || aggregatedUsage.note.includes('Real-time')
 ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
 : 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
 }`}>
 <div className="flex items-center gap-2">
 {aggregatedUsage.note.includes('Realtidsdata') || aggregatedUsage.note.includes('Real-time') ? (
 <CheckCircleIcon className="w-5 h-5 text-green-600" />
 ) : (
 <SignalSlashIcon className="w-5 h-5 text-yellow-600" />
 )}
 <p className={`text-sm ${aggregatedUsage.note.includes('Realtidsdata') || aggregatedUsage.note.includes('Real-time')
 ? 'text-green-700 dark:text-green-300'
 : 'text-yellow-700 dark:text-yellow-300'
 }`}>
 {aggregatedUsage.note}
 </p>
 </div>
 </div>
 )}

 {/* Rate Plans */}
 {ratePlans.length > 0 && (
 <div className="bg-bgElevated rounded-xl shadow-sm border border-border p-6">
 <h3 className="text-lg font-semibold text-textDefault mb-4">
 {t('simCards.ratePlans')}
 </h3>
 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
 {ratePlans.map((plan) => (
 <div key={plan.id} className="p-4 bg-bgContainer rounded-lg">
 <p className="font-medium text-textDefault">{plan.name}</p>
 {plan.description && (
 <p className="text-sm text-textSecondary mt-1">{plan.description}</p>
 )}
 {plan.type && (
 <span className="inline-block mt-2 px-2 py-1 text-xs bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded">
 {plan.type}
 </span>
 )}
 </div>
 ))}
 </div>
 </div>
 )}

 {/* Refresh Button */}
 <div className="flex justify-end">
 <button
 onClick={() => loadAggregatedUsage(true)}
 disabled={loading}
 className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
 >
 <ArrowPathIcon className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
 {t('simCards.refresh')}
 </button>
 </div>
 </div>
 )}

 {/* SIM Cards Tab */}
 {activeTab === 'simcards' && (
 <div className="space-y-6">
 {/* Filters */}
 <div className="bg-bgElevated rounded-xl shadow-sm border border-border p-4">
 <div className="flex flex-wrap gap-4">
 <div className="flex-1 min-w-[200px]">
 <div className="relative">
 <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-textTertiary" />
 <input
 type="text"
 value={searchTerm}
 onChange={(e) => setSearchTerm(e.target.value)}
 placeholder={t('simCards.searchPlaceholder')}
 className="w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-bgBase text-textDefault focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
 />
 </div>
 </div>

 <div className="w-48">
 <div className="relative">
 <FunnelIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-textTertiary" />
 <select
 value={statusFilter}
 onChange={(e) => handleStatusFilterChange(e.target.value)}
 className="w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-bgBase text-textDefault focus:ring-2 focus:ring-indigo-500 focus:border-transparent appearance-none"
 >
 <option value="">{t('simCards.allStatuses')}</option>
 {Object.entries(SIM_STATUS_CONFIG).map(([key, config]) => {
 const sKeyMap: Record<string, string> = {
 ACTIVATED: 'simCards.statusActivated',
 ACTIVATION_READY: 'simCards.statusActivationReady',
 DEACTIVATED: 'simCards.statusDeactivated',
 INVENTORY: 'simCards.statusInventory',
 RETIRED: 'simCards.statusRetired',
 TEST_READY: 'simCards.statusTestReady',
 };
 return (
 <option key={key} value={key}>
 {sKeyMap[key] ? t(sKeyMap[key]) : config.label}
 </option>
 );
 })}
 </select>
 </div>
 </div>

 <button
 onClick={loadSimCards}
 disabled={loading}
 className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
 >
 <ArrowPathIcon className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
 {t('simCards.fetch')}
 </button>
 </div>
 </div>

 {/* SIM Cards Table */}
 <div className="bg-bgElevated rounded-xl shadow-sm border border-border overflow-hidden">
 <div className="overflow-x-auto">
 <table className="min-w-full divide-y divide-border">
 <thead className="bg-bgContainer">
 <tr>
 <th className="px-6 py-3 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">
 ICCID
 </th>
 <th className="px-6 py-3 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">
 MSISDN
 </th>
 <th className="px-6 py-3 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">
 Status
 </th>
 <th className="px-6 py-3 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">
 {t('simCards.ratePlan')}
 </th>
 <th className="px-6 py-3 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">
 {t('simCards.dataMB')}
 </th>
 <th className="px-6 py-3 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">
 SMS
 </th>
 <th className="px-6 py-3 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">
 {t('simCards.activated')}
 </th>
 </tr>
 </thead>
 <tbody className="bg-bgElevated divide-y divide-border">
 {loading ? (
 <tr>
 <td colSpan={7} className="px-6 py-12 text-center">
 <ArrowPathIcon className="w-8 h-8 animate-spin text-indigo-600 mx-auto" />
 <p className="mt-2 text-textSecondary">
 {t('simCards.loading')}
 </p>
 </td>
 </tr>
 ) : simCards.length === 0 ? (
 <tr>
 <td colSpan={7} className="px-6 py-12 text-center text-textSecondary">
 {t('simCards.noSimCards')}
 </td>
 </tr>
 ) : (
 simCards.map((sim) => (
 <tr
 key={sim.iccid}
 className="hover:bg-bgBase/50 transition-colors"
 >
 <td className="px-6 py-4 whitespace-nowrap">
 <span className="font-mono text-sm text-textDefault">
 {sim.iccid}
 </span>
 </td>
 <td className="px-6 py-4 whitespace-nowrap">
 <span className="text-sm text-textDefault">
 {sim.msisdn || '-'}
 </span>
 </td>
 <td className="px-6 py-4 whitespace-nowrap">
 {getStatusBadge(sim.status)}
 </td>
 <td className="px-6 py-4 whitespace-nowrap">
 <span className="text-sm text-textSecondary">
 {sim.ratePlan || '-'}
 </span>
 </td>
 <td className="px-6 py-4 whitespace-nowrap">
 <span className="text-sm text-textDefault">
 {sim.usage?.ctdDataUsage
 ? (sim.usage.ctdDataUsage / 1024 / 1024).toFixed(2)
 : '0'}
 </span>
 </td>
 <td className="px-6 py-4 whitespace-nowrap">
 <span className="text-sm text-textDefault">
 {sim.usage?.ctdSMSUsage || 0}
 </span>
 </td>
 <td className="px-6 py-4 whitespace-nowrap">
 <span className="text-sm text-textSecondary">
 {formatDate(sim.dateActivated)}
 </span>
 </td>
 </tr>
 ))
 )}
 </tbody>
 </table>
 </div>

 {/* Pagination */}
 {totalCount > pageSize && (
 <div className="px-6 py-4 border-t border-border flex items-center justify-between">
 <p className="text-sm text-textSecondary">
 {t('simCards.showingRange')
 .replace('{start}', String(((page - 1) * pageSize) + 1))
 .replace('{end}', String(Math.min(page * pageSize, totalCount)))
 .replace('{total}', String(totalCount))}
 </p>
 <div className="flex gap-2">
 <button
 onClick={goToPreviousPage}
 disabled={page === 1}
 className="px-3 py-1 text-sm bg-bgBase rounded hover:bg-bgBase disabled:opacity-50 disabled:cursor-not-allowed"
 >
 {t('simCards.previous')}
 </button>
 <button
 onClick={goToNextPage}
 disabled={page * pageSize >= totalCount}
 className="px-3 py-1 text-sm bg-bgBase rounded hover:bg-bgBase disabled:opacity-50 disabled:cursor-not-allowed"
 >
 {t('simCards.next')}
 </button>
 </div>
 </div>
 )}
 </div>
 </div>
 )}

 {/* Reports Tab */}
 {activeTab === 'reports' && (
 <div className="space-y-6">
 <div className="bg-bgElevated rounded-xl shadow-sm border border-border p-6">
 <h3 className="text-lg font-semibold text-textDefault mb-4">
 {t('simCards.createUsageReport')}
 </h3>
 <p className="text-textSecondary mb-6">
 {t('simCards.createUsageReportDesc')}
 </p>

 <button
 onClick={createReport}
 disabled={loading}
 className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
 >
 <DocumentArrowDownIcon className="w-5 h-5" />
 {loading ? t('simCards.creatingReport') : t('simCards.createReport')}
 </button>
 </div>

 {aggregatedUsage && (
 <div className="bg-bgElevated rounded-xl shadow-sm border border-border p-6">
 <h3 className="text-lg font-semibold text-textDefault mb-4">
 {t('simCards.latestReport')}
 </h3>
 <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
 <div className="p-4 bg-bgContainer rounded-lg">
 <p className="text-sm text-textSecondary">
 {t('simCards.deviceCount')}
 </p>
 <p className="text-xl font-bold text-textDefault">
 {aggregatedUsage.totalDevices.toLocaleString()}
 </p>
 </div>
 <div className="p-4 bg-bgContainer rounded-lg">
 <p className="text-sm text-textSecondary">
 {t('simCards.totalData')}
 </p>
 <p className="text-xl font-bold text-textDefault">
 {aggregatedUsage.totalDataUsageGB.toFixed(2)} GB
 </p>
 </div>
 <div className="p-4 bg-bgContainer rounded-lg">
 <p className="text-sm text-textSecondary">
 {t('simCards.totalSms')}
 </p>
 <p className="text-xl font-bold text-textDefault">
 {aggregatedUsage.totalSmsUsage.toLocaleString()}
 </p>
 </div>
 <div className="p-4 bg-bgContainer rounded-lg">
 <p className="text-sm text-textSecondary">
 {t('simCards.period')}
 </p>
 <p className="text-xl font-bold text-textDefault">
 {aggregatedUsage.billingCycle}
 </p>
 </div>
 </div>
 </div>
 )}

 <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
 <p className="text-sm text-yellow-800 dark:text-yellow-200">
 <strong>{t('simCards.noteLabel')}</strong>{' '}
 {t('simCards.invoiceNote')}
 </p>
 </div>
 </div>
 )}
 </div>
 </div>
 );
};

export default SimCardManagement;
